import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import __noop from '@atlaskit/ds-lib/noop';
import PageHeader from '@atlaskit/page-header';
import { RadioGroup } from '@atlaskit/radio';
import TextField from '@atlaskit/textfield';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Link } from 'react-router-dom';
import { demandPartnerServices } from '@shared/api';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { setDemandPartnerProfiles } from '@shared/store/partnerSlice';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import {
    formatDateTime,
    renderCompletion,
    renderStatus
} from '@features/partnerManagement/components/partnerListCellHelpers';
import PartnerListTable from '@features/partnerManagement/components/PartnerListTable';
import type { DemandPartnerProfile } from '@shared/api/services/partner.service';
import { LuExternalLink } from 'react-icons/lu';

const dialogOverlayStyles = {
    alignItems: 'center',
    background: 'var(--app-overlay)',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    padding: token('space.400'),
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 1000
} as const;

const dialogCardStyles = {
    background: token('color.background.input'),
    border: `1px solid ${token('color.border')}`,
    borderRadius: '18px',
    maxWidth: '520px',
    padding: token('space.300'),
    width: '100%'
} as const;

const dividerStyles = {
    background: token('color.border'),
    height: '1px',
    width: '100%'
} as const;

const dpTypeOptions = [
    { label: 'SSP', value: 'SSP' },
    { label: 'DSP', value: 'DSP' },
    { label: 'EXCHANGE', value: 'EXCHANGE' }
] as const;

const rtbVersionOptions = [
    { label: '2.5', value: '2.5' },
    { label: '2.6', value: '2.6' }
] as const;

type AddDemandPartnerDialogProps = {
    isOpen: boolean;
    isSaving: boolean;
    onClose: () => void;
    onSave: (payload: { dpName: string; dpType: string; rtbVersion: string }) => void;
};

function AddDemandPartnerDialog({
    isOpen,
    isSaving,
    onClose,
    onSave
}: AddDemandPartnerDialogProps) {
    const [dpName, setDpName] = useState('');
    const [dpType, setDpType] = useState('');
    const [rtbVersion, setRtbVersion] = useState('');

    useEffect(() => {
        if (isOpen) {
            setDpName('');
            setDpType('');
            setRtbVersion('');
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    return (
        <Box style={dialogOverlayStyles}>
            <Box style={dialogCardStyles}>
                <Stack space="space.250">
                    <Inline spread="space-between" alignBlock="center">
                        <Text size="large" weight="medium">
                            Add Demand Partner
                        </Text>
                    </Inline>

                    <Box style={dividerStyles} />

                    <Stack space="space.100">
                        <Text color="color.text.subtle" size="small">
                            Demand Partner Name
                        </Text>
                        <TextField
                            aria-label="Demand partner name"
                            value={dpName}
                            onChange={(event) => setDpName(event.currentTarget.value)}
                            placeholder="Enter demand partner name"
                        />
                    </Stack>

                    <Stack space="space.100">
                        <Text id="demand-partner-type-label" color="color.text.subtle" size="small">
                            DP Type
                        </Text>
                        <RadioGroup
                            aria-labelledby="demand-partner-type-label"
                            name="demand-partner-type"
                            options={[...dpTypeOptions]}
                            value={dpType}
                            onChange={(event) => setDpType(event.currentTarget.value)}
                        />
                    </Stack>

                    <Stack space="space.100">
                        <Text id="demand-partner-rtb-version-label" color="color.text.subtle" size="small">
                            RTB Version
                        </Text>
                        <RadioGroup
                            aria-labelledby="demand-partner-rtb-version-label"
                            name="demand-partner-rtb-version"
                            options={[...rtbVersionOptions]}
                            value={rtbVersion}
                            onChange={(event) => setRtbVersion(event.currentTarget.value)}
                        />
                    </Stack>

                    <Inline space="space.100">
                        <Button
                            appearance="primary"
                            isLoading={isSaving}
                            onClick={() => {
                                const trimmedName = dpName.trim();
                                const trimmedType = dpType.trim();
                                const trimmedRtbVersion = rtbVersion.trim();

                                if (!trimmedName || !trimmedType || !trimmedRtbVersion) {
                                    return;
                                }

                                onSave({
                                    dpName: trimmedName,
                                    dpType: trimmedType,
                                    rtbVersion: trimmedRtbVersion
                                });
                            }}
                        >
                            Save
                        </Button>
                        <Button appearance="danger" isDisabled={isSaving} onClick={onClose}>
                            Cancel
                        </Button>
                    </Inline>
                </Stack>
            </Box>
        </Box>
    );
}

const demandPartnerColumns: ColumnDef<DemandPartnerProfile, unknown>[] = [
    {
        id: 'dpId',
        header: 'DP ID',
        accessorKey: 'dpId',
        sortingFn: 'alphanumeric',
        cell: ({ row }) => (
            <Link
                to={`/partner/dp/${row.original.dpId}`}
                style={{
                    color: 'var(--ds-link, #0c66e4)',
                    fontWeight: 600,
                    textDecoration: 'none'
                }}
            >
                {row.original.dpId}
            </Link>
        )
    },
    {
        id: 'dpName',
        header: 'Name',
        accessorKey: 'dpName',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'config',
        header: 'Config',
        cell: ({ row }) => (
            <Link
                to={`/partner/dp/${row.original.dpId}/config`}
                style={{
                    color: 'var(--ds-link, #0c66e4)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    textDecoration: 'none'
                }}
            >
                View Config <LuExternalLink size={12} />
            </Link>
        )
    },
    {
        id: 'dpType',
        header: 'DP Type',
        accessorKey: 'dpType',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'rtbVersion',
        header: 'RTB Version',
        accessorKey: 'rtbVersion',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => renderStatus(row.original.status)
    },
    {
        id: 'pocCompleted',
        header: 'POC',
        accessorKey: 'pocCompleted',
        cell: ({ row }) => renderCompletion(row.original.pocCompleted)
    },
    {
        id: 'configCompleted',
        header: 'Config',
        accessorKey: 'configCompleted',
        cell: ({ row }) => renderCompletion(row.original.configCompleted)
    },
    {
        id: 'companyCompleted',
        header: 'Company',
        accessorKey: 'companyCompleted',
        cell: ({ row }) => renderCompletion(row.original.companyCompleted)
    },
    {
        id: 'createdBy',
        header: 'Created By',
        accessorKey: 'createdBy',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'createdAt',
        header: 'Created At',
        accessorKey: 'createdAt',
        cell: ({ row }) => formatDateTime(row.original.createdAt)
    },
    {
        id: 'updatedBy',
        header: 'Updated By',
        accessorKey: 'updatedBy',
        sortingFn: 'alphanumeric'
    },
    {
        id: 'updatedAt',
        header: 'Updated At',
        accessorKey: 'updatedAt',
        cell: ({ row }) => formatDateTime(row.original.updatedAt)
    }
];

const DemandPartner: () => JSX.Element = () => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const demandPartners = useAppSelector((state) => Object.values(state.partner.demandPartnerProfiles));
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const breadcrumbs = (
      <Breadcrumbs onExpand={__noop}>
        <BreadcrumbsItem text="Dashboard" key="Dashboard" href="/home" />
        <BreadcrumbsItem text="Demand Partner" key="Demand Partner" />
      </Breadcrumbs>
    );
    const { data } = useQuery({
        queryKey: ['allDpList'],
        queryFn: () => demandPartnerServices.getAllDPprofileDetails()
    });

    const onboardMutation = useMutation({
        mutationFn: (payload: { dpName: string; dpType: string; rtbVersion: string }) =>
            demandPartnerServices.onboardDp(payload),
        onSuccess: async () => {
            const nextDemandPartners = await demandPartnerServices.getAllDPprofileDetails();
            dispatch(setDemandPartnerProfiles(nextDemandPartners));
            void queryClient.invalidateQueries({ queryKey: ['allDpList'] });
            setIsAddDialogOpen(false);
        }
    });

    useEffect(() => {
        if (data) {
            dispatch(setDemandPartnerProfiles(data));
        }
    }, [data, dispatch]);

    const actionsContent = (
        <ButtonGroup label="Content actions">
            <Button appearance="primary" onClick={() => setIsAddDialogOpen(true)}>
                Add demand partner
            </Button>
        </ButtonGroup>
    );

    return (
        <Stack space="space.300" xcss={{ width: '100%' }}>
            <PageHeader breadcrumbs={breadcrumbs} actions={actionsContent}>
                Demand Partner Management
            </PageHeader>
            <AddDemandPartnerDialog
                isOpen={isAddDialogOpen}
                isSaving={onboardMutation.isPending}
                onClose={() => setIsAddDialogOpen(false)}
                onSave={(payload) => onboardMutation.mutate(payload)}
            />
            <PartnerListTable
                items={demandPartners}
                columns={demandPartnerColumns}
                emptyMessage="No demand partners found"
            />
        </Stack>)

};

export default DemandPartner;
